import React, { useCallback } from 'react'
import { TrayThumbnailCard } from '@/components/app/panels/right-panel/tray/tray-thumbnail-card'
import { useTemplatePlacementOptional } from '@/contexts/template-placement-context'
import { usePlacedTemplateId } from '@/hooks/use-placed-template-id'
import { startTrayDragFromPointer } from '@/lib/placement/start-tray-drag-from-pointer'
import { TEMPLATE_TRAY_DRAGGING_THUMB_OPACITY } from '@/lib/templates/template-placement-config'
import type { TemplateListItem } from '@/lib/templates/types/template-list-item'
import { trayThumbnailBoxClassName } from '@/lib/tray/tray-thumbnail-box'

/** The API's spread previews are about 3:2, so the two-up box matches them instead of the tray's
 *  squarer default. */
const TEMPLATE_TWO_COLUMN_BOX_CLASS = 'aspect-[3/2]'

type TemplateThumbnailProps = {
  template: TemplateListItem
  showTemplateName?: boolean
  /** 1 = single wide row; 2 = compact two-column grid — mirrors `BackgroundThumbnail`. Defaults
   *  to 1. */
  columns?: 1 | 2
  /** Reports hover/focus preview open state for the host panel's Escape priority chain. */
  onPreviewOpenChange?: (open: boolean) => void
}

/**
 * Templates-tray thumbnail — a spread preview, one per row in the single-column layout
 * (`--panel-single-grid-height`) and a compact two-up grid beyond it. The API's previews are about
 * 3:2 (library thumbnails 291×194, custom ones 500×324), so the two-up box is `aspect-[3/2]`
 * rather than Backgrounds' squarer `aspect-[120/97.5]`, and the image uses `imageFit="contain"` in
 * the card and the enlarged preview: `cover` would trim the outer edges of the left and right
 * pages. The enlarged hover/focus preview only opens in two-column mode, since the single-column
 * row is already large enough to read (US-14).
 *
 * Placement is drag-and-drop only (US-13): pointer down starts a drag onto the spread, Enter/Space
 * starts a keyboard carry, and there is no click-to-place. The card is outlined while it is the
 * template most recently placed on the spread on screen. With no placement provider mounted the
 * card is browse-and-preview only, exposed as an image rather than a button.
 */
export function TemplateThumbnail({
  template,
  showTemplateName = false,
  columns = 1,
  onPreviewOpenChange,
}: TemplateThumbnailProps) {
  const placement = useTemplatePlacementOptional()
  const placedTemplateId = usePlacedTemplateId()
  const isDraggingThis =
    placement?.draggingTemplateId === template.id && placement.isDraggingTemplate

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, card: HTMLDivElement) => {
      if (!placement) return

      startTrayDragFromPointer(e, card, (clientX, clientY, rect, pointerId, _pointerType, el) =>
        placement.beginPointerDrag(template, clientX, clientY, rect, pointerId, el)
      )
    },
    [placement, template]
  )

  const handleActivate = useCallback(
    (rect: DOMRect) => placement?.beginKeyboardCarry(template, rect),
    [placement, template]
  )

  return (
    <TrayThumbnailCard
      itemKey={template.id}
      label={template.templateName}
      ariaLabel={
        placement
          ? `${template.templateName}. Press Enter or Space to pick up for placement on the spread.`
          : template.templateName
      }
      imageUrl={template.urlSmallPreview}
      previewUrl={template.urlLargePreview}
      showCaption={showTemplateName}
      highlighted={placedTemplateId === template.id}
      columns={columns}
      mediaAspectClassName={trayThumbnailBoxClassName(columns, TEMPLATE_TWO_COLUMN_BOX_CLASS)}
      imageFit="contain"
      opacity={isDraggingThis ? TEMPLATE_TRAY_DRAGGING_THUMB_OPACITY : 1}
      previewEnabled={columns === 2 && !placement?.isDraggingTemplate}
      onPreviewOpenChange={onPreviewOpenChange}
      role={placement ? 'button' : 'img'}
      onPointerDown={placement ? handlePointerDown : undefined}
      onActivate={placement ? handleActivate : undefined}
    />
  )
}
